import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import * as assignmentsService from '../services/assignments.service';
import type { AssignmentDTO, AssignmentSortOrder } from '../types/assignments.types';
import { useToastStore } from '../../../store/toast.store';

function parseSortOrder(value: string | null, fallback: AssignmentSortOrder): AssignmentSortOrder {
  return value === 'desc' || value === 'asc' ? value : fallback;
}

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

function defaultStatus(taskType: string): string {
  return taskType === 'download' || taskType === 'message_send' ? 'all' : 'submitted';
}

export function useAssignmentList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const source = searchParams.get('source') || '';
  const agentId = searchParams.get('agent_id') || '';
  const agentName = searchParams.get('agent_name') || '';
  const taskType = searchParams.get('taskType') || '';
  const marketId = searchParams.get('marketId') || '';
  const status = searchParams.get('status') || defaultStatus(taskType);
  const taskId = searchParams.get('taskId') || '';
  const userId = searchParams.get('userId') || '';
  const userKeyword = searchParams.get('userKeyword') || '';
  const taskKeyword = searchParams.get('taskKeyword') || '';
  const platform = searchParams.get('platform') || '';
  const appleIdMatchResult = searchParams.get('appleIdMatchResult') || '';
  const order = parseSortOrder(searchParams.get('order'), 'asc');
  const page = parseInt(searchParams.get('page') || '1', 10) || 1;
  const pageSize = parseInt(searchParams.get('pageSize') || '20', 10) || 20;

  const lastLoaded = useRef(0);
  const loadVersion = useRef(0);
  const [items, setItems] = useState<AssignmentDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const addToast = useToastStore((s) => s.addToast);

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v !== null) next.set(k, v);
        else next.delete(k);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async (
    p: number,
    m: string,
    s: string,
    t: string,
    uid: string,
    uk: string,
    tk: string,
    tt: string,
    pl: string,
    appleMatch: string,
    sortOrder: AssignmentSortOrder,
    silent = false,
  ) => {
    const version = ++loadVersion.current;
    if (!silent) { setLoading(true); setError(null); }
    try {
      const result = await assignmentsService.listAssignments({
        page: p, pageSize, source, agentId, agentName, marketId: m, status: s, taskId: t, userId: uid, userKeyword: uk, taskKeyword: tk, taskType: tt, platform: pl, appleIdMatchResult: appleMatch, order: sortOrder,
      });
      if (version !== loadVersion.current) return;
      lastLoaded.current = Date.now();
      setItems(result.items);
      setTotal(result.total);
    } catch (e) {
      if (!silent && version === loadVersion.current) setError(resolveError(e));
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  }, [pageSize, source, agentId, agentName]);

  useEffect(() => {
    load(page, marketId, status, taskId, userId, userKeyword, taskKeyword, taskType, platform, appleIdMatchResult, order);
  }, [load, page, marketId, status, taskId, userId, userKeyword, taskKeyword, taskType, platform, appleIdMatchResult, order]);

  const setAgentName = useCallback((value: string) => {
    updateParams({ agent_name: value || null, page: null });
  }, [updateParams]);

  const setMarketId = useCallback((m: string) => {
    updateParams({ marketId: m || null, page: null });
  }, [updateParams]);

  const setStatus = useCallback((s: string) => {
    updateParams({ status: s, page: null });
  }, [updateParams]);

  const setTaskId = useCallback((t: string) => {
    updateParams({ taskId: t || null, page: null });
  }, [updateParams]);

  const setUserId = useCallback((value: string) => {
    updateParams({ userId: value || null, page: null });
  }, [updateParams]);

  const setUserKeyword = useCallback((value: string) => {
    updateParams({ userKeyword: value.trim() || null, page: null });
  }, [updateParams]);

  const setTaskKeyword = useCallback((value: string) => {
    updateParams({ taskKeyword: value.trim() || null, page: null });
  }, [updateParams]);

  const setTaskType = useCallback((t: string) => {
	const nextStatus = defaultStatus(t);
	updateParams({ taskType: t || null, status: nextStatus, page: null });
  }, [updateParams]);

  const setOrder = useCallback((value: AssignmentSortOrder) => {
    updateParams({ order: value === 'asc' ? null : value, page: null });
  }, [updateParams]);

  const setPlatform = useCallback((p: string) => {
    updateParams({ platform: p || null, page: null });
  }, [updateParams]);

  const setAppleIdMatchResult = useCallback((value: string) => {
    updateParams({ appleIdMatchResult: value || null, page: null });
  }, [updateParams]);

  const setPage = useCallback((p: number) => {
    updateParams({ page: p > 1 ? String(p) : null });
  }, [updateParams]);

  const setPageSize = useCallback((value: number) => {
    updateParams({ pageSize: String(value), page: null });
  }, [updateParams]);

  const resetFilters = useCallback(() => {
	const nextStatus = defaultStatus(taskType);
	const preserved: Record<string, string> = { status: nextStatus };
    if (taskType) preserved.taskType = taskType;
    setSearchParams(preserved, { replace: true });
  }, [setSearchParams, taskType]);

  const reload = useCallback(() => {
    load(page, marketId, status, taskId, userId, userKeyword, taskKeyword, taskType, platform, appleIdMatchResult, order);
  }, [load, page, marketId, status, taskId, userId, userKeyword, taskKeyword, taskType, platform, appleIdMatchResult, order]);

  const refreshImages = useCallback(() => {
    void load(page, marketId, status, taskId, userId, userKeyword, taskKeyword, taskType, platform, appleIdMatchResult, order, true);
  }, [load, page, marketId, status, taskId, userId, userKeyword, taskKeyword, taskType, platform, appleIdMatchResult, order]);

  useEffect(() => {
    if (loading || !items.some((item) => item.message_evidence?.length)) return;
    const refreshIfExpired = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastLoaded.current >= 4 * 60 * 1000) {
        lastLoaded.current = Date.now();
        refreshImages();
      }
    };
    const timer = window.setInterval(refreshIfExpired, 30_000);
    window.addEventListener('focus', refreshIfExpired);
    document.addEventListener('visibilitychange', refreshIfExpired);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshIfExpired);
      document.removeEventListener('visibilitychange', refreshIfExpired);
    };
  }, [items, loading, refreshImages]);

  const review = useCallback(async (
    assignmentId: number,
    action: 'approve' | 'reject',
    note: string,
  ) => {
    setReviewing(true);
    try {
      const item = items.find((entry) => entry.id === assignmentId);
      if (item?.task_type === 'message_send') {
        await assignmentsService.reviewMessageAssignment(assignmentId, action, note, note);
      } else {
        await assignmentsService.reviewAssignment(assignmentId, action, note);
      }
      addToast('success', action === 'approve' ? '审核通过' : '已拒绝');
      reload();
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setReviewing(false);
    }
  }, [addToast, items, reload]);

  const reviewMany = useCallback(async (
    assignmentIds: number[],
    action: 'approve' | 'reject',
    note: string,
  ) => {
    if (assignmentIds.length === 0) return;
    setReviewing(true);
    try {
      const messageIds = assignmentIds.filter((assignmentId) => (
        items.find((entry) => entry.id === assignmentId)?.task_type === 'message_send'
      ));
      const normalIds = assignmentIds.filter((assignmentId) => !messageIds.includes(assignmentId));
      if (normalIds.length > 0) {
        await assignmentsService.reviewAssignments(normalIds, action, note);
      }
      for (const assignmentId of messageIds) {
        await assignmentsService.reviewMessageAssignment(assignmentId, action, note, note);
      }
      addToast('success', `${action === 'approve' ? '批量通过' : '批量拒绝'} ${assignmentIds.length} 条`);
      reload();
    } catch (e) {
      addToast('error', resolveError(e));
      reload();
    } finally {
      setReviewing(false);
    }
  }, [addToast, items, reload]);

  const deleteMany = useCallback(async (assignmentIds: number[]) => {
    if (assignmentIds.length === 0) return;
    setDeleting(true);
    try {
      for (const assignmentId of assignmentIds) {
        await assignmentsService.deleteAssignment(assignmentId);
      }
      addToast('success', `已删除 ${assignmentIds.length} 条审核记录`);
      reload();
    } catch (e) {
      addToast('error', resolveError(e));
      reload();
    } finally {
      setDeleting(false);
    }
  }, [addToast, reload]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    refreshImages,
    items, total, page, pageSize, totalPages,
    agentName, marketId, status, taskId, userId, userKeyword, taskKeyword, taskType, platform, appleIdMatchResult, order, loading, error, reviewing, deleting,
    setAgentName, setPage, setPageSize, setMarketId, setStatus, setTaskId, setUserId, setUserKeyword, setTaskKeyword, setTaskType, setPlatform, setAppleIdMatchResult, setOrder, resetFilters, reload, review, reviewMany, deleteMany,
  };
}
